import React from "react";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Paper,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import HeadsetMicRoundedIcon from "@mui/icons-material/HeadsetMicRounded";
import KeyboardRoundedIcon from "@mui/icons-material/KeyboardRounded";
import MicOffRoundedIcon from "@mui/icons-material/MicOffRounded";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import PhoneInTalkRoundedIcon from "@mui/icons-material/PhoneInTalkRounded";
import PauseCircleOutlineRoundedIcon from "@mui/icons-material/PauseCircleOutlineRounded";
import PlayCircleOutlineRoundedIcon from "@mui/icons-material/PlayCircleOutlineRounded";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import WifiRoundedIcon from "@mui/icons-material/WifiRounded";
import controls, {
  acquireAgentMicrophoneStream,
  prepareAgentMicrophone,
  runAgentConnectionDiagnostic,
  switchAgentInput,
  switchAgentOutput,
  syncPlaybackDevices,
} from "./controls";
import {showSnackbar} from "./Snackbar";

const signalTone = (quality, phase) => {
  if (phase === "reconnecting") return {label: "Reconnecting", color: "warning"};
  if (quality === "poor" || quality === "lost") return {label: "Network poor", color: "error"};
  if (quality === "good" || quality === "excellent") return {label: "Network stable", color: "success"};
  return {label: "Checking network", color: "default"};
};

export default function AgentStatusBar({
  queueCount,
  activeCallCount = 0,
  callStatus,
  activityState = "available",
  onActivityChange,
  onMicPrepared,
}) {
  const [audioAnchor, setAudioAnchor] = React.useState(null);
  const [shortcutAnchor, setShortcutAnchor] = React.useState(null);
  const [devices, setDevices] = React.useState([]);
  const [microphones, setMicrophones] = React.useState([]);
  const [micTesting, setMicTesting] = React.useState(false);
  const [micLevel, setMicLevel] = React.useState(0);
  const [speakerTesting, setSpeakerTesting] = React.useState(false);
  const [connectionTesting, setConnectionTesting] = React.useState(false);
  const micTestRef = React.useRef({stream: null, context: null, frame: null, timeout: null});
  const network = signalTone(callStatus.agentNetworkQuality, callStatus.phase);
  const isBusy = Boolean(callStatus.activeCallerId);
  const needsAttention = callStatus.phase === "failed" || callStatus.micPermission === "denied";
  const onBreak = activityState === "break";
  const availabilityLabel = needsAttention ? "Needs attention" :
    isBusy ? "On a call" : onBreak ? "On break" : "Available";
  const availabilityColor = needsAttention ? "warning" :
    isBusy ? "primary" : onBreak ? "warning" : "success";

  const stopMicTest = React.useCallback((completed = false) => {
    const test = micTestRef.current;
    if (test.frame) window.cancelAnimationFrame(test.frame);
    if (test.timeout) window.clearTimeout(test.timeout);
    test.stream?.getTracks().forEach((track) => track.stop());
    test.context?.close?.().catch?.(() => {});
    micTestRef.current = {stream: null, context: null, frame: null, timeout: null};
    setMicTesting(false);
    setMicLevel(0);
    if (completed) showSnackbar("Microphone test complete.");
  }, []);

  React.useEffect(() => () => stopMicTest(false), [stopMicTest]);

  const prepareMic = async () => {
    const ready = await prepareAgentMicrophone();
    onMicPrepared?.();
    showSnackbar(
        ready ? "Microphone is ready." : "Allow microphone access in your browser settings.",
        ready ? "success" : "warning",
    );
  };

  const testMicrophone = async () => {
    if (micTesting) {
      stopMicTest(false);
      return;
    }
    if (!navigator.mediaDevices?.getUserMedia) {
      showSnackbar("Microphone testing is unavailable in this browser.", "warning");
      return;
    }

    try {
      const activeTrack = controls.microphoneTrack?.mediaStreamTrack;
      const stream = activeTrack ?
        new MediaStream([activeTrack.clone()]) :
        await acquireAgentMicrophoneStream();
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) throw new Error("unsupported");
      const context = new AudioCtx();
      if (context.state === "suspended") await context.resume();
      const analyser = context.createAnalyser();
      analyser.fftSize = 256;
      context.createMediaStreamSource(stream).connect(analyser);
      const samples = new Uint8Array(analyser.fftSize);

      micTestRef.current.stream = stream;
      micTestRef.current.context = context;
      setMicTesting(true);
      controls.microphonePermission = "ready";
      onMicPrepared?.();

      const measure = () => {
        analyser.getByteTimeDomainData(samples);
        let sum = 0;
        for (let index = 0; index < samples.length; index += 1) {
          const sample = (samples[index] - 128) / 128;
          sum += sample * sample;
        }
        setMicLevel(Math.min(100, Math.round(Math.sqrt(sum / samples.length) * 360)));
        micTestRef.current.frame = window.requestAnimationFrame(measure);
      };
      measure();
      micTestRef.current.timeout = window.setTimeout(() => stopMicTest(true), 10000);
    } catch (_) {
      stopMicTest(false);
      controls.microphonePermission = "denied";
      onMicPrepared?.();
      showSnackbar("Microphone test failed. Check browser permission and try again.", "warning");
    }
  };

  const openAudioMenu = async (event) => {
    // React clears currentTarget after the click handler yields, so retain the
    // button before scanning devices and open the menu without waiting.
    const anchor = event.currentTarget;
    setAudioAnchor(anchor);
    setMicrophones(controls.microphoneDevices || []);

    const playbackDevices = await syncPlaybackDevices();
    setDevices(playbackDevices);
    setMicrophones(controls.microphoneDevices || []);
  };

  const closeAudioMenu = () => {
    stopMicTest(false);
    setAudioAnchor(null);
  };

  const chooseOutput = async (device) => {
    const changed = await switchAgentOutput(device);
    showSnackbar(
        changed ? `Using ${(device.label || "system output").toLowerCase()}.` : "Output switching is not supported in this browser.",
        changed ? "success" : "warning",
    );
  };

  const chooseInput = async (device) => {
    const changed = await switchAgentInput(device);
    onMicPrepared?.();
    showSnackbar(
        changed ?
          `Using ${(device.label || "system microphone").toLowerCase()}.` :
          "That microphone could not send audio. Choose another input.",
        changed ? "success" : "warning",
    );
  };

  const playSpeakerTest = async () => {
    if (speakerTesting) return;
    setSpeakerTesting(true);
    let context;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) throw new Error("unsupported");
      context = new AudioCtx();
      if (controls.preferredOutputDeviceId && context.setSinkId) {
        await context.setSinkId(controls.preferredOutputDeviceId);
      }
      if (context.state === "suspended") await context.resume();
      const gain = context.createGain();
      gain.gain.value = 0.0001;
      gain.connect(context.destination);
      const now = context.currentTime;
      gain.gain.exponentialRampToValueAtTime(0.2, now + 0.04);
      gain.gain.setValueAtTime(0.2, now + 0.58);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.78);
      [660, 880].forEach((frequency, index) => {
        const oscillator = context.createOscillator();
        oscillator.frequency.value = frequency;
        oscillator.connect(gain);
        oscillator.start(now + (index * 0.3));
        oscillator.stop(now + 0.42 + (index * 0.3));
      });
      await new Promise((resolve) => window.setTimeout(resolve, 850));
      showSnackbar(`Speaker test played through ${controls.preferredOutputLabel || "system output"}.`);
    } catch (_) {
      showSnackbar("Speaker test failed. Check your output volume and browser audio permission.", "warning");
    } finally {
      await context?.close?.().catch?.(() => {});
      setSpeakerTesting(false);
    }
  };

  const testConnection = async () => {
    if (connectionTesting) return;
    setConnectionTesting(true);
    try {
      const result = await runAgentConnectionDiagnostic();
      showSnackbar(
          result.ok ?
            "Connection test passed: WebRTC, TURN, and audio are ready." :
            (result.message ||
              "Connection test found an issue. Try another network."),
          result.ok ? "success" : "warning",
      );
    } catch (_) {
      showSnackbar(
          "Connection test could not finish. Check your network and retry.",
          "warning",
      );
    } finally {
      setConnectionTesting(false);
    }
  };

  const compactButtonSx = {
    minWidth: 0,
    px: {xs: 0.8, sm: 1},
    minHeight: 32,
    fontSize: "0.72rem",
    whiteSpace: "nowrap",
    flex: {xs: 1, sm: "0 0 auto"},
  };

  return (
    <Paper
      component="section"
      aria-label="Agent call tools"
      elevation={0}
      sx={{
        alignSelf: "flex-start",
        maxWidth: {xs: "calc(100% - 24px)", md: "calc(100% - 40px)"},
        mx: {xs: 1.5, md: 2.5},
        mt: {xs: 1.2, md: 2},
        px: 0.8,
        py: 0.65,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "10px",
        bgcolor: (theme) => theme.palette.mode === "dark" ? "rgba(24,34,31,0.94)" : "rgba(255,255,255,0.82)",
        overflow: "hidden",
      }}
    >
      <Stack direction={{xs: "column", sm: "row"}} alignItems={{xs: "stretch", sm: "center"}} spacing={0.55}>
        <Box sx={{display: "grid", gridTemplateColumns: {xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(4, auto)"}, gap: 0.55, minWidth: 0}}>
          <Chip size="small" label={`${queueCount} waiting`} sx={{fontWeight: 600, minWidth: 0}} />
          <Tooltip title={`${activeCallCount} ${activeCallCount === 1 ? "call is" : "calls are"} currently being answered`}>
            <Chip
              size="small"
              icon={<PhoneInTalkRoundedIcon />}
              color={activeCallCount > 0 ? "primary" : "default"}
              label={`${activeCallCount} active`}
              aria-label={`${activeCallCount} ${activeCallCount === 1 ? "call" : "calls"} currently being answered`}
              sx={{fontWeight: 600, minWidth: 0}}
            />
          </Tooltip>
          <Chip size="small" color={availabilityColor} label={availabilityLabel} sx={{fontWeight: 600, minWidth: 0}} />
          <Tooltip title={isBusy ? network.label : "Network will be measured during a call"}>
            <Chip
              size="small"
              color={isBusy ? network.color : "default"}
              icon={<WifiRoundedIcon />}
              label={isBusy ? network.label.replace("Network ", "") : "Network ready"}
              sx={{fontWeight: 600, minWidth: 0, flex: "1 1 auto", "& .MuiChip-label": {overflow: "hidden", textOverflow: "ellipsis"}}}
            />
          </Tooltip>
        </Box>

        <Divider orientation="vertical" flexItem sx={{mx: 0.25, my: 0.35, display: {xs: "none", sm: "block"}}} />

        <Stack direction="row" alignItems="center" spacing={0.55} sx={{width: {xs: "100%", sm: "auto"}, minWidth: 0}}>

          <Tooltip title="Open your microphone and show its live input level">
            <Button
              size="small"
              onClick={testMicrophone}
              color={micTesting ? "success" : ["denied", "unavailable"].includes(callStatus.micPermission) ? "warning" : "inherit"}
              startIcon={micTesting ? <GraphicEqRoundedIcon /> : ["denied", "unavailable"].includes(callStatus.micPermission) ? <MicOffRoundedIcon /> : <MicRoundedIcon />}
              sx={compactButtonSx}
            >
              {micTesting ? `Mic ${micLevel}%` : "Test mic"}
            </Button>
          </Tooltip>

          <Tooltip title="Play a short sound through the selected output">
            <Button
              size="small"
              onClick={playSpeakerTest}
              disabled={speakerTesting}
              startIcon={<VolumeUpRoundedIcon />}
              sx={compactButtonSx}
            >
              {speakerTesting ? "Playing..." : "Speaker"}
            </Button>
          </Tooltip>

          <Tooltip title="Choose audio output">
            <Button size="small" onClick={openAudioMenu} startIcon={<HeadsetMicRoundedIcon />} aria-label="Audio setup" sx={compactButtonSx}>
              Audio
            </Button>
          </Tooltip>
          <Tooltip title={onBreak ?
            "Resume receiving callers" : "Pause new calls while on break"}>
            <Button
              size="small"
              disabled={isBusy}
              onClick={() => onActivityChange?.(
                  onBreak ? "available" : "break",
              )}
              color={onBreak ? "success" : "inherit"}
              startIcon={onBreak ?
                <PlayCircleOutlineRoundedIcon /> :
                <PauseCircleOutlineRoundedIcon />}
              sx={compactButtonSx}
            >
              {onBreak ? "Resume" : "Break"}
            </Button>
          </Tooltip>
          <Popover
            open={Boolean(audioAnchor)}
            anchorEl={audioAnchor}
            onClose={closeAudioMenu}
            anchorOrigin={{vertical: "bottom", horizontal: "left"}}
            transformOrigin={{vertical: "top", horizontal: "left"}}
          >
          <Stack sx={{p: 1.2, minWidth: 260}} spacing={0.7}>
            <Typography variant="subtitle2" sx={{fontWeight: 650}}>Audio check</Typography>
            <Button startIcon={<MicRoundedIcon />} onClick={testMicrophone} color={micTesting ? "success" : "inherit"} sx={{justifyContent: "flex-start"}}>
              {micTesting ? "Stop microphone test" : "Test microphone"}
            </Button>
            {micTesting && (
              <Box sx={{px: 1, pb: 0.5}}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    title={controls.preferredInputLabel}
                    sx={{maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap"}}
                  >
                    {controls.preferredInputLabel}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{micLevel}%</Typography>
                </Stack>
                <Box sx={{height: 6, mt: 0.5, borderRadius: 999, bgcolor: "action.hover", overflow: "hidden"}}>
                  <Box sx={{height: "100%", width: `${Math.max(3, micLevel)}%`, bgcolor: micLevel > 3 ? "success.main" : "text.disabled", transition: "width 70ms linear"}} />
                </Box>
              </Box>
            )}
            <Button startIcon={<VolumeUpRoundedIcon />} onClick={playSpeakerTest} disabled={speakerTesting} sx={{justifyContent: "flex-start"}}>
              {speakerTesting ? "Playing test sound..." : "Test speaker"}
            </Button>
            <Button
              startIcon={<WifiRoundedIcon />}
              onClick={testConnection}
              disabled={connectionTesting || isBusy}
              sx={{justifyContent: "flex-start"}}
            >
              {connectionTesting ? "Testing connection..." :
                "Test call connection"}
            </Button>
            <Divider />
            <Typography variant="overline" color="text.secondary" sx={{px: 1, fontWeight: 650}}>Microphone input</Typography>
            {(microphones.length ? microphones : [{deviceId: "default", label: "System microphone"}]).map((device) => (
              <Button
                key={device.deviceId}
                onClick={() => chooseInput(device)}
                variant={controls.preferredInputDeviceId === device.deviceId ? "outlined" : "text"}
                sx={{justifyContent: "flex-start", textTransform: "none"}}
              >
                {device.label || "System microphone"}
              </Button>
            ))}
            <Divider />
            <Typography variant="overline" color="text.secondary" sx={{px: 1, fontWeight: 650}}>Audio output</Typography>
            {(devices.length ? devices : [{deviceId: "default", label: "System output"}]).map((device) => (
              <Button
                key={device.deviceId}
                onClick={() => chooseOutput(device)}
                variant={controls.preferredOutputDeviceId === device.deviceId ? "outlined" : "text"}
                sx={{justifyContent: "flex-start", textTransform: "none"}}
              >
                {device.label || "System output"}
              </Button>
            ))}
            <Button onClick={prepareMic} startIcon={<MicRoundedIcon />} sx={{justifyContent: "flex-start"}}>Refresh microphone permission</Button>
          </Stack>
          </Popover>

          <Tooltip title="Keyboard shortcuts">
            <IconButton size="small" onClick={(event) => setShortcutAnchor(event.currentTarget)} aria-label="Keyboard shortcuts" sx={{flexShrink: 0, display: {xs: "none", sm: "inline-flex"}}}>
              <KeyboardRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Popover
            open={Boolean(shortcutAnchor)}
            anchorEl={shortcutAnchor}
            onClose={() => setShortcutAnchor(null)}
            anchorOrigin={{vertical: "bottom", horizontal: "left"}}
            transformOrigin={{vertical: "top", horizontal: "left"}}
          >
          <Stack spacing={0.7} sx={{p: 1.4, minWidth: 210}}>
            <Typography variant="subtitle2" sx={{fontWeight: 650}}>Keyboard shortcuts</Typography>
            {[["↑ / ↓", "Select caller"], ["Enter", "Answer selected"], ["A", "Answer first"], ["M", "Mute or unmute"], ["E", "End call (press twice)"]].map(([key, label]) => (
              <Stack key={key} direction="row" justifyContent="space-between" spacing={2}>
                <Typography variant="caption" sx={{fontWeight: 700}}>{key}</Typography>
                <Typography variant="caption" color="text.secondary">{label}</Typography>
              </Stack>
            ))}
          </Stack>
          </Popover>
        </Stack>
      </Stack>
    </Paper>
  );
}
